/**
 * Lecture des indicateurs du bilan chiffré (ADR 0015) dans une Timeline, et statut de sourçage de leur mécanique. Pur.
 * Séparé de `scorecard.ts` pour être partagé avec l'attribution sans import circulaire.
 */
import { PCT } from "./state.js";
import type { MechanismStatus, ParamSet, ScorecardIndicatorDef, Timeline } from "./types.js";

/** Valeur d'un indicateur à l'année `year` (dernière année simulée ≤ `year`) ; null si aucune année ou clé absente. */
export function indicatorValue(def: ScorecardIndicatorDef, t: Timeline, year: number): number | null {
  const upto = t.years.filter((y) => y.year <= year);
  const last = upto[upto.length - 1];
  if (!last) return null;
  const read = (k: Record<string, number>): number | null => {
    const v = k[def.kpi];
    if (v === undefined || !Number.isFinite(v)) return null;
    if (!def.per_gdp) return v;
    const gdp = k["gdp_bn"];
    return gdp ? (v / gdp) * PCT : null;
  };
  const sign = def.sign ?? 1;
  if (def.aggregate === "mean") {
    const vs = upto.map((y) => read(y.kpis));
    if (vs.some((v) => v === null)) return null;
    return (sign * (vs as number[]).reduce((a, x) => a + x, 0)) / vs.length;
  }
  const v = read(last.kpis);
  return v === null ? null : sign * v;
}

/** Valeur de départ : `Timeline.start` (fin de l'année de base) quand la clé y figure ; sinon le chiffre de la baseline. */
export function indicatorStart(def: ScorecardIndicatorDef, t: Timeline, figure: number | null): number | null {
  const s = t.start;
  if (s && s[def.kpi] !== undefined && def.aggregate === "final") {
    const v = s[def.kpi]!;
    if (!def.per_gdp) return (def.sign ?? 1) * v;
    const gdp = s["gdp_bn"];
    return gdp ? (def.sign ?? 1) * (v / gdp) * PCT : null;
  }
  return figure;
}

/** Paramètres désignés par la liste (motif `préfixe*` résolu contre `params`, ordre de `params`). */
export function resolveMechanismParams(params: ParamSet, ids: string[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    if (id.endsWith("*")) {
      const prefix = id.slice(0, -1);
      for (const k of Object.keys(params)) if (k.startsWith(prefix) && !out.includes(k)) out.push(k);
    } else if (!out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Un paramètre est « sourcé » s'il porte une source (≠ "TODO") et n'est pas une convention de jeu ; « deux sources » s'il en a
 * au moins deux (`source` + `sources[]`). Un identifiant absent de `params` compte comme non sourcé (erreur de contenu visible).
 */
export function mechanismStatus(params: ParamSet, ids: string[]): { status: MechanismStatus; total: number; sourced: number; two_sources: number; unsourced: string[] } {
  const all = resolveMechanismParams(params, ids);
  let sourced = 0;
  let two = 0;
  const unsourced: string[] = [];
  for (const id of all) {
    const p = params[id];
    const ok = !!p && p.source !== "TODO" && p.status !== "convention_de_jeu" && p.status !== "TODO";
    if (!ok) {
      unsourced.push(id);
      continue;
    }
    sourced++;
    if (1 + (p.sources?.length ?? 0) >= 2) two++;
  }
  const status: MechanismStatus = all.length > 0 && sourced === all.length ? "mecanique_sourcee" : sourced === 0 ? "mecanique_non_sourcee" : "mecanique_partielle";
  return { status, total: all.length, sourced, two_sources: two, unsourced };
}
